import { TAB_RAIL_REACH } from '../../../../../../_tabs/TabButton';
import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import { COVERAGE_WRAPPER_SIDE_PADDING } from '../../../../../../agencies/quotes/PrimeX/customizeQuote/components/CoverageWrapper';
import {
  SELECT_CONTENT_HEIGHT,
  SELECT_INPUT_PADDING,
} from '../../../../../../../themes/_base/_base.overrides';

/**
 * Vertical padding shared by the underwriter view's header tiles (quote
 * header, rater options). `CardWrapper` pads twice — CardContent and an inner
 * Box — so the tiles zero the first and put this on the second.
 */
export const UW_TILE_PADDING_Y = '0.75rem';

/**
 * How far the fixed tab rail (Notes, Comparables, Change Log) reaches into a
 * full-width tile: its reach from the page edge, less the page gutter. Tiles
 * keep their full width and pad their content by this on the right, so no
 * right-aligned text sits under the rail.
 */
export const UW_RAIL_CLEARANCE = `calc(${TAB_RAIL_REACH} - var(--tile-gutter, 1rem))`;

/**
 * Where right-aligned controls end in the sticky header: the rail clearance
 * plus the Rater Options tab row's own right padding. UW Preferences ends
 * here, and the proposed-changes banner pads Accept all to the same edge.
 */
export const UW_HEADER_ROW_INSET_RIGHT = '1rem';
export const UW_HEADER_CONTROLS_END = `calc(${UW_RAIL_CLEARANCE} + ${UW_HEADER_ROW_INSET_RIGHT})`;

/**
 * CardContent's padding for the header tiles. The inner Box keeps its own
 * horizontal padding, which is the gap between the text and the rail.
 */
export const UW_TILE_CARD_STYLES = {
  paddingTop: 0,
  paddingBottom: 0,
  paddingRight: UW_RAIL_CLEARANCE,
};

/**
 * Vertical padding of every row in the coverage and modifier panels, single-
 * and multi-rater alike, so the two trees read the same height.
 */
export const UW_ROW_PADDING_Y = '0.5rem';

/** Coverage and modifier panels: the group title in each panel's header row. */
export const UW_PANEL_TITLE_FONT_SIZE = '1rem';

/** A component or section card's title ("Cyber", "Policy Endorsements"). */
export const UW_SECTION_TITLE_FONT_SIZE = '1.2rem';

/** Padding inside a multi-rater component card, around its header and panels. */
export const UW_COMPONENT_CARD_PADDING = '0.75rem';

/** A multi-rater (component) row's and panel header's own horizontal padding. */
export const UW_COMPONENT_ROW_PADDING_X = '0.5rem';

/** Coverage and modifier panels: the column headings beside the title. */
export const UW_PANEL_HEADING_FONT_SIZE = '0.9rem';

/** Coverage and modifier panels: the Category Premium / Running Aggregate strip. */
export const UW_PANEL_TOTALS_FONT_SIZE = '1rem';

/** Coverage and modifier panels: vertical padding of the header row. */
export const UW_PANEL_HEADER_PADDING_Y = '0.5rem';

/**
 * Coverage and modifier panels: the header row's floor. None — the content and
 * its padding decide, so a one-line title gets a one-line row.
 */
export const UW_PANEL_HEADER_MIN_HEIGHT = 'auto';

/** Coverage and modifier panels: title and column headings, semibold not bold. */
export const UW_PANEL_HEADING_WEIGHT = 600;

/**
 * Where a coverage panel's content ends on the right: the Category Premium
 * strip's right padding. A row's accept/reject pair ends here too, so the
 * pair and the totals beneath it share one edge.
 */
export const UW_PANEL_CONTENT_END = '1.5rem';

/** A single-rater coverage row's own horizontal padding. */
export const UW_ROW_PADDING_X = '0.25rem';

/** The space between one coverage or modifier panel and the next. */
export const UW_PANEL_GAP = '1rem';

/** The space between one tile and the next, top of the view to the footer. */
export const UW_TILE_GAP = '0.5rem';

/**
 * The coverage grid's input block (Retention and the optional columns after
 * it) starts this far into its cell, in the panel header, every row, and the
 * globals above them.
 */
export const UW_INPUTS_INSET = '2.5rem';

/**
 * The coverage grid's left and right edges, measured from the edge of the
 * view's column: the coverage wrapper's padding plus a row's own. The globals
 * row pads to these so it lines up with the panels below.
 */
export const UW_GRID_INSET_LEFT = `calc(${COVERAGE_WRAPPER_SIDE_PADDING} + ${UW_ROW_PADDING_X})`;
export const UW_GRID_INSET_RIGHT = `calc(${COVERAGE_WRAPPER_SIDE_PADDING} + ${UW_RAIL_CLEARANCE} + ${UW_ROW_PADDING_X})`;

/**
 * The underwriter globals' selects: filled, no outline — the look Carrier and
 * Retroactive Date already have, so the row reads as one set of controls.
 */
export const UW_BORDERLESS_SELECT_SX = {
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
};

/** The globals' field labels, sized like the coverage panel headings below. */
export const UW_GLOBAL_LABEL_STYLE = { fontSize: UW_PANEL_HEADING_FONT_SIZE };

/**
 * The multi-rater globals bring controls of their own — Shared Limit and the
 * defence-cost limit are outlined currency fields with their own labels. This
 * sets every control in that row to the Selects' look: label size, no outline,
 * filled and rounded, and a Select's pad and content height. Scoped to the row
 * because those fields are shared with other rater headers.
 */
export const UW_GLOBALS_CONTROLS_SX: SxProps<Theme> = {
  // A label that sets no padding of its own (the date field's) gets the one
  // every other label here sets inline, so all share one label line.
  // One label line for every field: `TextFieldBase` sets its own 1.33rem line
  // height, which put the date field's box ~2px above its neighbours'.
  '& label': {
    ...UW_GLOBAL_LABEL_STYLE,
    padding: '0 0 0.25rem',
    lineHeight: 1.4,
  },
  // The theme pads every MuiFormControl by 1.5px; the TextField-based
  // controls sat that much lower than the Selects, which have none.
  '& .MuiFormControl-root': { padding: 0 },
  // Shared Limit's clip adornment is a fixed 3rem tall and stretched its
  // field past the Selects'. Here it fills whatever height the input sets.
  '& .MuiInputAdornment-root': {
    height: 'auto',
    maxHeight: 'none',
    alignSelf: 'stretch',
  },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiInputBase-root': {
    backgroundColor: (theme: Theme) => theme.palette.background.active,
    borderRadius: '5px',
  },
  // Text inputs only: a Select keeps its own right pad for the caret.
  '& .MuiInputBase-input:not(.MuiSelect-select)': {
    border: 'none',
    paddingTop: SELECT_INPUT_PADDING,
    paddingBottom: SELECT_INPUT_PADDING,
    height: SELECT_CONTENT_HEIGHT,
    boxSizing: 'content-box',
  },
};

/**
 * True inside the underwriter view. Components shared with the agency quote
 * views (rater-x listings, component cards) read it to apply the underwriter
 * sizing here and keep their original look everywhere else; `styled` rules
 * use `UW_VIEW_SCOPE` from `themes/viewScopes` for the same purpose.
 */
export const UwViewContext = React.createContext(false);

export const useIsUwView = () => React.useContext(UwViewContext);

/**
 * The rater-x cards and listings (component cards, policy and amendatory
 * endorsements, modifier categories, and the coverage and modifier panels
 * nested in them) are MUI Accordions. Off the underwriter view they keep their
 * original styles, including the `-1.25rem` offsets that cancel MUI's expanded
 * summary margin. In the view, that margin is zeroed at the source instead (the
 * offsets assumed a 16px root and drifted at 12 and 14), the accordions lose
 * their own shadow and divider line, and spacing comes from the panel gap.
 */
const UW_FLAT_ACCORDION_SX = {
  padding: 0,
  margin: 0,
  boxShadow: 'none',
  '&.Mui-expanded': { margin: 0 },
  '&::before': { display: 'none' },
};

const UW_FLAT_SUMMARY_SX = {
  padding: 0,
  minHeight: 0,
  '&.Mui-expanded': { minHeight: 0, margin: 0 },
  '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
    { margin: 0 },
};

export const useUwAccordionStyles = () => {
  const isUwView = useIsUwView();

  if (!isUwView) {
    return {
      isUwView,
      cardStyle: { padding: '1rem' },
      accordionSx: { padding: 0 },
      summarySx: { padding: 0, '&.Mui-expanded': { margin: 0 } },
      detailsSx: { padding: 0 },
      /** A nested category section. */
      sectionStyle: { marginTop: '-1.25rem' },
      /** The rows card under a nested panel's header. */
      nestedCardStyle: { marginTop: '-1.25rem' },
    };
  }

  return {
    isUwView,
    cardStyle: { padding: UW_COMPONENT_CARD_PADDING },
    accordionSx: UW_FLAT_ACCORDION_SX,
    summarySx: UW_FLAT_SUMMARY_SX,
    // A panel gap between a card's header and the first panel inside it.
    detailsSx: { padding: 0, paddingTop: UW_PANEL_GAP },
    // Panels stack a panel gap apart, collapsed or not.
    sectionStyle: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: UW_PANEL_GAP,
      marginBottom: UW_PANEL_GAP,
    },
    nestedCardStyle: { marginTop: 0, marginBottom: 0 },
  };
};
